import { cva } from "class-variance-authority";
import { isArray, isObject, isString } from "lodash-es";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ListFieldsType } from "src/api/config";
import { notificationsListName } from "src/api/config/notifications.config";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { getItem } from "src/modules/spItems/actions";
import { getItemsRequest } from "src/modules/spItems/api";
import { modifiedItemsState } from "src/modules/spItems/selectors";
import { selectCurrentUser } from "src/modules/users/selector";
import { FiltersStateType } from "src/pages/DashboardPage/acadia/type";
import { RootState } from "src/store/rootReducer";
import { isMobile } from "src/utils/device";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { nameFromEmail, shortNameFromEmail } from "src/utils/users.utils";
import { twMerge } from "tailwind-merge";
import { TableColumnWithIcon } from "../../pages/DashboardPage/acadia/mock";
import { EmptyList } from "../EmptyList";
import Loading from "../Loading";
import SortIcon from "../ui-icons/table/SortIcon";
import { TableHeaderColumnProps } from "../ui/table/Table";
import { UserAvatarItemStore } from "../ui/TGUserAvatarsList";
import { GetNotificationDrawer } from "./notificationTables/GetNotificationTable";
import { GetNotificationDrawerMobile } from "./notificationTables/GetNotificationTable.mobile";
import { SendOneNotificationDrawer } from "./notificationTables/SendNotificationTable";
import { notificationStatuses } from "src/api/config/systemSwitch";

type TableHeadType = keyof Pick<
  ListFieldsType[typeof notificationsListName],
  "DocumentCode" | "Document" | "NotificationType" | "Created" | "Employee" | "Author" | "Completed" | "VersionDocument"
>;

const tableHeaderContent2: Record<TableHeadType, TableColumnWithIcon> = {
  DocumentCode: {
    title: "Код документа",
    sort: true,
    style: { width: "10%" },
  },
  VersionDocument: {
    title: "Версия документа",
    sort: false,
    style: { width: "10%" },
  },
  Document: {
    title: "Наименование",
    sort: true,
    style: { width: "15%" },
  },
  NotificationType: {
    title: "Тип уведомления",
    sort: true,
    style: { width: "10%" },
  },
  Created: {
    title: "Дата отправления уведомления",
    sort: true,
    style: { width: "15%" },
  },
  Employee: {
    title: "Кому отправлено",
    sort: true,
    style: { width: "15%" },
  },
  Author: {
    title: "Кем отправлено",
    sort: true,
    style: { width: "15%" },
  },
  Completed: {
    title: "Дата завершения",
    sort: true,
    style: { width: "10%" },
  },
};

export function isFiltersObjectEmpty(filters: FiltersStateType) {
  return Object.entries(filters).some(([key, value]) => {
    if (isArray(value)) {
      return value.length > 0;
    } else if (isObject(value) && key === "dateForRevision") {
      return value.date.start !== null || value.date.end !== null || value.required;
    } else {
      return !!value;
    }
  });
}

function NotifyReportTable({
  items,
  loading,
}: {
  items: ListFieldsType[typeof notificationsListName][];
  loading: boolean;
}) {
  return <Table header={tableHeaderContent2} body={items} loading={loading} />;
}

export default NotifyReportTable;

function Table({
  header,
  body,
  loading,
}: {
  header: typeof tableHeaderContent2;
  body: ListFieldsType[typeof notificationsListName][];
  loading: boolean;
}) {
  const [sortedField, setSortedField] = useState<keyof ListFieldsType[typeof notificationsListName] | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | "default">("default");

  const handleSort = (field: keyof ListFieldsType[typeof notificationsListName]) => {
    if (sortedField === field) {
      setSortDirection((prevDirection) =>
        prevDirection === "asc" ? "desc" : prevDirection === "desc" ? "default" : "asc",
      );
    } else {
      setSortedField(field);
      setSortDirection("asc");
    }
  };

  const sortedTableBodyContent = useMemo(() => {
    if (!sortedField || sortDirection === "default") {
      return body;
    }

    const sortedData = [...body];

    sortedData.sort((a, b) => {
      if (a[sortedField] && b[sortedField]) {
        if (sortedField === "Document" && a[sortedField].Title && b[sortedField].Title) {
          return sortDirection === "asc"
            ? a[sortedField].Title.toLowerCase().localeCompare(b[sortedField].Title)
            : b[sortedField].Title.toLowerCase().localeCompare(a[sortedField].Title);
        }

        if (sortedField === "NotificationType") {
          return sortDirection === "asc"
            ? a[sortedField].ID - b[sortedField].ID
            : b[sortedField].ID - a[sortedField].ID;
        }

        if (sortedField === "Author" || sortedField === "Employee") {
          return sortDirection === "asc"
            ? a[sortedField].Name.localeCompare(b[sortedField].Name)
            : b[sortedField].Name.localeCompare(a[sortedField].Name);
        }

        const valueA = a[sortedField];
        const valueB = b[sortedField];

        if (isObject(valueA) && isString(valueA) && isString(valueB) && sortedField === "DocumentCode") {
          return sortDirection === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
        }

        if (typeof valueA === "string" && typeof valueB === "string") {
          return sortDirection === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
        } else if (typeof valueA === "boolean" && typeof valueB === "boolean") {
          return sortDirection === "asc" ? Number(valueA) - Number(valueB) : Number(valueB) - Number(valueA);
        } else if (moment.isMoment(valueA) && moment.isMoment(valueB)) {
          return sortDirection === "asc" ? valueA.unix() - valueB.unix() : valueB.unix() - valueA.unix();
        }
      }

      return 0;
    });

    return sortedData;
  }, [sortedField, sortDirection, body]) as ListFieldsType[typeof notificationsListName][];

  return (
    <FlexCol className="w-full">
      <TableList
        head={header}
        body={sortedTableBodyContent}
        sort={handleSort}
        sortDirection={sortDirection}
        sortedField={sortedField}
        loading={loading}
      />
    </FlexCol>
  );
}

function TableList({
  head,
  body,
  sort,
  sortDirection,
  sortedField,
  loading,
}: {
  head: typeof tableHeaderContent2;
  body: ListFieldsType[typeof notificationsListName][];
  sort: (f: keyof ListFieldsType[typeof notificationsListName]) => void;
  sortedField: keyof ListFieldsType[typeof notificationsListName] | null;
  sortDirection: "asc" | "desc" | "default";
  loading: boolean;
}) {
  const currentUser = useAppSelector((state: RootState) => selectCurrentUser(state));

  const itemRef = useRef<ListFieldsType[typeof notificationsListName] | null>(null);

  const [query, setQuery] = useSearchParams();

  function handleClickNotificationCard(item: ListFieldsType[typeof notificationsListName]) {
    const newSearchParams = new URLSearchParams(query);

    const isAuthor = item.AuthorEmail === currentUser.email;

    if (
      item.NotificationType.ID === notificationStatuses.approval ||
      item.NotificationType.ID === notificationStatuses.cancel ||
      item.NotificationType.ID === notificationStatuses.approved ||
      item.NotificationType.ID === notificationStatuses.revision
    ) {
      if (item.NotificationID) {
        newSearchParams.set("notificationID", isAuthor ? item.NotificationID.ID.toString() : item.ID.toString());
      }

      if (!!item.VersionDocument) {
        let versionID = item.VersionDocument.ID;

        newSearchParams.set("version", versionID.toString());
      }

      newSearchParams.set("documentCard", item.Document.ID.toString());
    } else {
      newSearchParams.set("notificationCard", item.ID.toString());
    }

    itemRef.current = item;

    setQuery(newSearchParams);
  }

  return (
    <div
      id="table-scroll"
      className={twMerge(
        "w-auto px-6 pb-5 overflow-x-auto bg-white rounded-lg table-custom shadow-custom scrollbar scrollbar-thumb-rounded-lg scrollbar-h-2 scrollbar-thumb-[#D1D5DB]",
        "max-h-[calc(100vh-156px)]",
      )}
    >
      <FlexRow className="w-auto min-w-[930px]">
        <div className="flex flex-col min-w-[1235px] grow">
          <FlexRow className="top-0 z-10 gap-x-4 px-6 pt-5 pb-5 -mx-6 -mt-5 bg-white border-b sticky-custom">
            {Object.keys(head).map((fieldKey) => {
              const key = fieldKey as TableHeadType;

              return (
                <TableHeaderColumn
                  key={fieldKey}
                  tableHeaderContent={head[key]}
                  sortDirection={sortedField === key ? sortDirection : "default"}
                  onClick={() => sort(key)}
                />
              );
            })}
          </FlexRow>
          <FlexRow className="mt-9">
            <FlexCol className="grow">
              {!loading ? (
                Array.isArray(body) && body.length > 0 ? (
                  <div className="divide-y table-wrap">
                    {body.map((item) => {
                      return (
                        <FlexRow
                          className="py-4 hover:bg-[#F3F4F6] hover:bg-opacity-50 rounded-lg  cursor-pointer gap-x-4  -mx-2 px-2"
                          key={item.ID}
                          onClick={() => handleClickNotificationCard(item)}
                        >
                          <FlexCol className="break-words text-[#637381]" style={{ width: "10%" }}>
                            {`${item.DocumentCode}`}
                          </FlexCol>
                          <FlexCol className="break-words text-darkblue" style={{ width: "10%" }}>
                            {(item.VersionDocument &&
                              (item.VersionDocument.Metadata.Ver || item.Document.Metadata.Ver || 1)) ||
                              1}
                          </FlexCol>
                          <FlexCol className="break-words text-darkblue" style={{ width: "15%" }}>
                            {item.VersionDocument ? item.VersionDocument.Title : item.Document.Title}
                          </FlexCol>

                          <FlexCol className="break-words text-[#637381]" style={{ width: "10%" }}>
                            {item.NotificationType?.Title || "Не определено"}
                          </FlexCol>
                          <FlexCol className="break-words text-[#637381]" style={{ width: "15%" }}>
                            {item.Created.clone().format("DD.MM.YYYY HH:mm")}
                          </FlexCol>
                          <FlexCol className="break-words text-[#637381]" style={{ width: "15%" }}>
                            <UserAvatarItemStore
                              single={true}
                              classImg="hidden"
                              item={{
                                name: nameFromEmail(item.EmployeeEmail),
                                shortName: shortNameFromEmail(item.EmployeeEmail),
                                email: item.EmployeeEmail,
                                avatar: null,
                              }}
                            />
                          </FlexCol>
                          <FlexCol className="break-words text-[#637381]" style={{ width: "15%" }}>
                            <UserAvatarItemStore
                              single={true}
                              classImg="hidden"
                              item={{
                                name: nameFromEmail(item.AuthorEmail),
                                shortName: shortNameFromEmail(item.AuthorEmail),
                                email: item.AuthorEmail,
                                avatar: null,
                              }}
                            />
                          </FlexCol>
                          <FlexCol className="break-words text-[#637381]" style={{ width: "10%" }}>
                            {item.Completed &&
                            item.NotificationType.ID !== notificationStatuses.acquainted &&
                            item.NotificationType.ID !== notificationStatuses.getOWD &&
                            item.NotificationType.ID !== notificationStatuses.approval &&
                            item.NotificationType.ID !== notificationStatuses.revision &&
                            !item.Title.includes("Отклонено") &&
                            item.Completed.isValid()
                              ? item.Completed.clone().format("DD.MM.YYYY HH:mm")
                              : "-----"}
                          </FlexCol>
                        </FlexRow>
                      );
                    })}
                  </div>
                ) : (
                  <EmptyList title="Здесь будут храниться все уведомления" />
                )
              ) : (
                <div className="pt-4">
                  <Loading />
                </div>
              )}
            </FlexCol>
          </FlexRow>
        </div>
      </FlexRow>

      {query.has("notificationCard") && <NotificationReportDrawer />}
    </div>
  );
}

function getRole(item: ListFieldsType[typeof notificationsListName], currentUser: any) {
  if (item.AuthorEmail === currentUser.email) {
    return "send";
  } else if (item.EmployeeEmail === currentUser.email) {
    return "get";
  } else {
    return "all";
  }
}

export function NotificationReportDrawer({ zIndex, customRole }: { zIndex?: number; customRole?: string }) {
  const currentUser = useAppSelector((state: RootState) => selectCurrentUser(state));
  const [query, setQuery] = useSearchParams();
  const [item, setItem] = useState<ListFieldsType[typeof notificationsListName] | null>(null);
  const dispatch = useAppDispatch();
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileDevice(isMobile());
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const id = +query.get("notificationCard")!;

  useEffect(() => {
    async function fetchData() {
      try {
        const { items } = await getItemsRequest({
          listName: notificationsListName,
          filters: { ID: [id] },
        });
        dispatch(getItem({ listName: notificationsListName, filters: { ID: [id] } }));

        const [item] = modifiedItemsState(items, notificationsListName);

        if (item.EmployeeEmail === currentUser.email) {
          customRole = "get";
        }

        setItem(item);
      } catch (e) {
        console.error("не найдены данные уведомения");
      }
    }

    if (currentUser.email) {
      fetchData();
    }
  }, [id, currentUser.email]);

  if (!item || !currentUser.email) return null;

  const role = getRole(item, currentUser);

  const GetNotificationDrawerComponent = isMobileDevice ? GetNotificationDrawerMobile : GetNotificationDrawer;

  if (item.Completed && item.Completed.isValid()) {
    if (item.NotificationType.ID === 5 || item.NotificationType.ID === 4) {
      return (
        <SendOneNotificationDrawer
          item={item}
          listName={notificationsListName}
          read={true}
          role={role}
          zIndex={zIndex}
        />
      );
    } else if (item.NotificationType.ID === 6) {
      return <GetNotificationDrawerComponent listName={notificationsListName} read={true} zIndex={zIndex} />;
    }
  }

  if (customRole && customRole === "get") {
    return <GetNotificationDrawerComponent listName={notificationsListName} zIndex={zIndex} />;
  } else if (role === "get") {
    return <GetNotificationDrawerComponent listName={notificationsListName} zIndex={zIndex} />;
  } else if (role === "send") {
    return <GetNotificationDrawerComponent listName={notificationsListName} zIndex={zIndex} />;
  } else {
    return <SendOneNotificationDrawer item={item} listName={notificationsListName} role={role} zIndex={zIndex} />;
  }
}

export function TableHeaderColumn({ tableHeaderContent, sortDirection, onClick }: TableHeaderColumnProps) {
  return (
    <FlexCol className="select-none" style={tableHeaderContent.style}>
      <FlexRow className="gap-x-2 items-center">
        {tableHeaderContent.icon && <FlexCol>{tableHeaderContent.icon}</FlexCol>}
        <FlexCol className="font-medium whitespace-pre-line w-fit">{tableHeaderContent.title}</FlexCol>
        {tableHeaderContent.sort && (
          <FlexCol className="w-auto cursor-pointer" onClick={onClick}>
            <SortIcon sortDirection={sortDirection} />
          </FlexCol>
        )}
      </FlexRow>
    </FlexCol>
  );
}

const criticalItemStyle = cva("block h-2 w-2 rounded-full", {
  variants: {
    active: {
      true: "bg-[#BA0C2F]",
      false: "bg-[#8899A8]",
    },
  },
  defaultVariants: {
    active: false,
  },
});

export function CriticallyListItem({ item }: { item: boolean }) {
  return (
    <FlexRow className="items-center space-x-1">
      <FlexCol>
        <span className={twMerge(criticalItemStyle({ active: item }))}></span>
      </FlexCol>
      <FlexCol>{item ? "" : "Не "}Критичный</FlexCol>
    </FlexRow>
  );
}
